import React from 'react';
import Loadable from 'react-loadable';
// eslint-disable-next-line import/no-cycle
import { PERMISSIONS, ROUTES } from './helpers/constants';

/**
 * Define routes
 *
 * @author aheshperera
 */

function Loading() {
  return <div>Loading...</div>;
}

const Dashboard = Loadable({
  loader: () => import('./views/Dashboard/dashboard'),
  loading: Loading,
});

const Files = Loadable({
  loader: () => import('./views/FileExplorer/fileExplorer'),
  loading: Loading,
});

const FilesTransferDetails = Loadable({
  loader: () => import('./views/FileTransferDetails/fileTransferDetailsReport'),
  loading: Loading,
});

const MassAgreementImport = Loadable({
  loader: () => import('./views/MassAgreement/massAgreementImport'),
  loading: Loading,
});

const Base12RequestImport = Loadable({
  loader: () => import('./views/MassAgreement/base12RequestImport'),
  loading: Loading,
});

const SASFdMaintenance = Loadable({
  loader: () => import('./views/MassAgreement/sasFDMaintenance/sasFdMaintenance'),
  loading: Loading,
});

const SasFdAddView = Loadable({
  loader: () => import('./views/MassAgreement/sasFDMaintenance/addView/sasFdAddView'),
  loading: Loading,
});

const OverageMaintenance = Loadable({
  loader: () => import('./views/OverageMaintenance/overageMaintenanceView'),
  loading: Loading,
});

const AgreementMaintenanceView = Loadable({
  loader: () => import('./views/AgreementMaintenance/agreementMaintenanceView'),
  loading: Loading,
});

const MassItemUpload = Loadable({
  loader: () => import('./views/MassItemUpload/massItemUploadView'),
  loading: Loading,
});

const FilesLog = Loadable({
  loader: () => import('./views/FilesLog/filesLog'),
  loading: Loading,
});

const EnterpriseCostView = Loadable({
  loader: () => import('./views/EnterpriseCost/costView'),
  loading: Loading,
});

const EnterpriseCostNewView = Loadable({
  loader: () => import('./views/EnterpriseCost/newCostView'),
  loading: Loading,
});

const VendorAgreementsLog = Loadable({
  loader: () => import('./views/PCPFilesLog/pcpAgreements/vendorAgreementsLog'),
  loading: Loading,
});

const VendorAgreementsPCPLog = Loadable({
  loader: () => import('./views/PCPFilesLog/pcpAgreements/vendorAgreementsPCPLog'),
  loading: Loading,
});

const PcpVendorCostPCPLog = Loadable({
  loader: () => import('./views/PCPFilesLog/pcpVendorCost/pcpVendorCostPCPLog'),
  loading: Loading,
});

const UserProfile = Loadable({
  loader: () => import('./baseContainer/user/profile/userProfile'),
  loading: Loading,
});

const ProfileManagement = Loadable({
  loader: () => import('./views/UserManagement/profileManagement/profileManagement'),
  loading: Loading,
  render(loaded, props) {
    const ProfileManagementComponent = loaded.default;

    const activeTabProps = props.match.path.includes(
      '/user-management/profile-management/edit/:id',
    )
      ? { activeTab: 'tab_3' }
      : { activeTab: props.location.state || 'tab_1' };

    return <ProfileManagementComponent {...props} {...activeTabProps} />;
  },
});

const UserDirectory = Loadable({
  loader: () => import('./views/UserManagement/userDirectory/userDirectory'),
  loading: Loading,
});

const CostAuditExplorer = Loadable({
  loader: () => import('./views/Reports/CostAudit/costAuditExplorer'),
  loading: Loading,
});

const AgreementReportCreationPanel = Loadable({
  loader: () => import('./views/Reports/Agreement/agreementReportCreationPanel'),
  loading: Loading,
});

const CostFileExplorer = Loadable({
  loader: () => import('./views/CostMaintenance/CostFile/costFileExplorer'),
  loading: Loading,
});

const AdhocCostChangeExplorer = Loadable({
  loader: () => import('./views/CostMaintenance/AdhocCostChange/adhocCostChangeExplorer'),
  loading: Loading,
});

const CatalogCostChangeExplorer = Loadable({
  loader: () => import('./views/CostMaintenance/CatalogCostChange/catalogCostChangeExplorer'),
  loading: Loading,
});

const FormulaGroupExplorer = Loadable({
  loader: () => import('./views/FormulaGroupMaintenance/formulaGroupExplorer'),
  loading: Loading,
});

const PriceModule = Loadable({
  loader: () => import('./views/CostMaintenance/PriceModule/priceModule'),
  loading: Loading,
});

const SegmentDataMaintenance = Loadable({
  loader: () => import('./views/CostMaintenance/SegmentDataMaintenance/segmentDataMaintenance'),
  loading: Loading,
});

const SegmentDataView = Loadable({
  loader: () => import('./views/CostMaintenance/SegmentDataMaintenance/segmentDataView'),
  loading: Loading,
});

const RequestOverview = Loadable({
  loader: () => import('./views/Dashboard/RequestOverview/requestOverview'),
  loading: Loading,
});

const CostReportsTabView = Loadable({
  loader: () => import('./views/Dashboard/RequestOverview/dataGrid/costReportsTabView'),
  loading: Loading,
});

const WorkspaceSearchView = Loadable({
  loader: () => import('./views/Search/workspaceSearch'),
  loading: Loading,
});

const MappingControlView = Loadable({
  loader: () => import('./views/Utilities/mappingControlView'),
  loading: Loading,
});

const ItemDataGridView = Loadable({
  loader: () => import('./views/Dashboard/RequestOverview/dataGrid/itemDataGridView'),
  loading: Loading,
});

const ItemSearchView = Loadable({
  loader: () => import('./views/Search/ItemSearch/itemSearch'),
  loading: Loading,
});

const FileUploadView = Loadable({
  loader: () => import('./views/OverageMaintenance/fileUploadView/fileUploadView'),
  loading: Loading,
});

// https://github.com/ReactTraining/react-router/tree/master/packages/react-router-config
const routes = [
  {
    path: '/enterprise-cost-view',
    name: 'Enterprise Cost View',
    exact: true,
    component: EnterpriseCostView,
    permissions: [PERMISSIONS.VIEW_ENTERPRISE_COST_DATA],
  },
  {
    ...ROUTES.COST_VIEW,
    exact: true,
    component: EnterpriseCostNewView,
    permissions: [PERMISSIONS.VIEW_COST_VIEW],
  },
  {
    path: '/',
    exact: true,
    name: 'Home',
    component: Dashboard,
    permissions: [PERMISSIONS.VIEW_COST_DATA_MANAGEMENT_DASHBOARD],
  },
  {
    path: '/requests/:id',
    exact: true,
    name: 'Request Overview',
    component: RequestOverview,
    permissions: [PERMISSIONS.VIEW_COST_DATA_MANAGEMENT_DASHBOARD],
  },
  {
    path: '/requests/:id/adhoc-cost-data',
    exact: true,
    name: 'Adhoc Cost Data',
    component: CostReportsTabView,
    permissions: [PERMISSIONS.VIEW_ADHOC_COST_CHANGE],
  },
  {
    path: '/requests/:id/commodity-cost-data',
    exact: true,
    name: 'Commodity Cost Data',
    component: CostReportsTabView,
    permissions: [PERMISSIONS.VIEW_ADHOC_COST_CHANGE],
  },
  {
    path: '/requests/:id/sygma-cost-data',
    exact: true,
    name: 'Sygma Cost Data',
    component: CostReportsTabView,
    permissions: [PERMISSIONS.VIEW_ADHOC_COST_CHANGE],
  },
  {
    path: '/requests/:id/bis-cost-data',
    exact: true,
    name: 'BIS Cost Data',
    component: CostReportsTabView,
    permissions: [PERMISSIONS.VIEW_ADHOC_COST_CHANGE],
  },
  {
    path: '/requests/:id/agreement-recalculation-cost-data',
    exact: true,
    name: 'Agreement Recalculation Cost Data',
    component: CostReportsTabView,
    permissions: [PERMISSIONS.AGREEMENT_RECALCULATION],
  },
  {
    path: '/requests/:id/item-group-item-association-data',
    exact: true,
    name: 'Item Assignment Data',
    component: ItemDataGridView,
    permissions: [PERMISSIONS.VIEW_ITEM_GROUP],
  },
  {
    path: '/requests/:id/market-group-cost-data',
    exact: true,
    name: 'Market Group Cost Data',
    component: CostReportsTabView,
    permissions: [PERMISSIONS.VIEW_MARKET_GROUP],
  },
  {
    path: '/requests/:id/market-group-item-association-data',
    exact: true,
    name: 'Market Group Item Data',
    component: ItemDataGridView,
    permissions: [PERMISSIONS.VIEW_MARKET_GROUP],
  },
  {
    path: '/bis-files',
    name: 'BIS File Explorer',
    component: Files,
    permissions: [PERMISSIONS.VIEW_BIS_FILE_EXPLORER],
  },
  {
    path: '/file-transfer-details',
    name: 'File Transfer Details',
    component: FilesTransferDetails,
    permissions: [PERMISSIONS.VIEW_FILE_TRANSFER_LOG],
  },
  {
    path: '/agreement-import',
    name: 'Agreement Request Import',
    component: MassAgreementImport,
    permissions: [PERMISSIONS.IMPORT_AGREEMENT_REQUEST],
  },
  {
    path: '/base12-import',
    name: 'Base 12 Request Import',
    component: Base12RequestImport,
    permissions: [PERMISSIONS.IMPORT_AGREEMENT_REQUEST],
  },
  {
    path: '/sas-fd-maintenance',
    exact: true,
    name: 'SAS & FD Maintenance',
    component: SASFdMaintenance,
    permissions: [PERMISSIONS.VIEW_SAS_FD, PERMISSIONS.CREATE_AND_EDIT_SAS_FD],
  },
  {
    path: '/sas-fd-maintenance/add',
    name: 'Add SAS & FD Values',
    component: SasFdAddView,
    permissions: [PERMISSIONS.CREATE_AND_EDIT_SAS_FD],
  },
  {
    path: '/overage-maintenance',
    exact: true,
    name: 'Overage Maintenance',
    component: OverageMaintenance,
    permissions: [PERMISSIONS.VIEW_OVERAGE_VALUES, PERMISSIONS.OVERAGE_CRUD],
  },
  {
    path: '/overage-maintenance/add',
    exact: true,
    name: 'File Import',
    component: FileUploadView,
    permissions: [PERMISSIONS.OVERAGE_CRUD],
  },
  {
    path: '/agreement-maintenance',
    name: 'Agreement Maintenance',
    component: AgreementMaintenanceView,
    permissions: [PERMISSIONS.AGREEMENT_VIEW],
  },
  {
    path: '/agreements/:id',
    name: 'Agreement Data',
    component: AgreementMaintenanceView,
    permissions: [PERMISSIONS.AGREEMENT_VIEW],
  },
  {
    ...ROUTES.AGREEMENT_DETAILS,
    component: AgreementMaintenanceView,
    permissions: [PERMISSIONS.AGREEMENT_VIEW]
  },
  {
    path: '/mass-item-upload',
    exact: true,
    name: 'Mass Item Upload',
    component: MassItemUpload,
    permissions: [PERMISSIONS.EDIT_ITEM_GROUP],
  },
  {
    path: '/file-explorer/costing',
    name: 'File Explorer / Costing',
    component: FilesLog,
    permissions: [PERMISSIONS.VIEW_EXPLORER],
  },
  {
    path: '/file-explorer-pcp/vendor-cost',
    name: 'File Explorer - PC Pricing  /  Vendor Cost',
    component: PcpVendorCostPCPLog,
    permissions: [PERMISSIONS.VIEW_PCP_EXPLORER],
  },
  {
    path: '/file-explorer-pcp/vendor-agreements',
    name: 'File Explorer - PC Pricing  /  Vendor Agreements',
    component: VendorAgreementsPCPLog,
    permissions: [PERMISSIONS.VIEW_EXPLORER],
  },
  {
    path: '/file-explorer/agreements',
    name: 'File Explorer / Agreements',
    component: VendorAgreementsLog,
    permissions: [PERMISSIONS.VIEW_EXPLORER],
  },
  { path: '/user/profile', name: 'My Profile', component: UserProfile },
  {
    path: '/reports/cost-audit',
    name: 'Reports / Cost Audit',
    component: CostAuditExplorer,
    permissions: [
      PERMISSIONS.EXECUTE_COST_AUDIT,
      PERMISSIONS.EXECUTE_LIMITED_COST_AUDIT,
    ],
  },
  {
    path: '/reports/agreement',
    name: 'Reports / Agreement',
    component: AgreementReportCreationPanel,
    permissions: [
      PERMISSIONS.EXECUTE_COST_AUDIT,
      PERMISSIONS.EXECUTE_LIMITED_COST_AUDIT,
    ],
  },
  {
    ...ROUTES.COST_FILE,
    component: CostFileExplorer,
    permissions: [
      PERMISSIONS.EXECUTE_COST_FILE,
      PERMISSIONS.EXECUTE_LIMITED_COST_FILE,
      PERMISSIONS.VIEW_COST_FILE_TEMPLATE,
      PERMISSIONS.MANAGE_COST_FILE_TEMPLATE,
    ],
  },
  {
    path: '/cost-maintenance/adhoc-cost-change',
    name: 'Cost Maintenance / Adhoc Cost Change',
    component: AdhocCostChangeExplorer,
    permissions: [PERMISSIONS.INITIATE_ADHOC_COST_CHANGE],
  },
  {
    path: '/cost-maintenance/catalog-cost-change',
    name: 'Cost Maintenance / Catalog Cost Change',
    component: CatalogCostChangeExplorer,
    permissions: [PERMISSIONS.INITIATE_CATALOG_COST_CHANGE],
  },
  {
    path: '/cost-maintenance/formula-group-cost-change',
    name: 'Cost Maintenance / Formula Group Cost Change',
    component: FormulaGroupExplorer,
    permissions: [PERMISSIONS.VIEW_MARKET_GROUP],
  },
  {
    ...ROUTES.PRICE_MODULE,
    component: PriceModule,
    permissions: [
      PERMISSIONS.PRICE_MODULE_UPLOAD,
      PERMISSIONS.PRICE_MODULE_VIEW,
    ],
  },
  {
    path: '/cost-maintenance/segment-data-maintenance',
    name: 'Cost Maintenance / Segment Data Maintenance',
    component: SegmentDataMaintenance,
    permissions: [PERMISSIONS.VIEW_SEGMENT_DETAILS]
  },
  {
    ...ROUTES.SEGMENT_DETAILS,
    component: SegmentDataView,
    permissions: [PERMISSIONS.VIEW_SEGMENT_DETAILS]
  },
  {
    path: '/user-management/user-directory',
    name: 'User Management / User Directory',
    component: UserDirectory,
    permissions: [PERMISSIONS.VIEW_USER_DIRECTORY],
  },
  {
    path: '/user-management/profile-management/edit/:id',
    exact: true,
    component: ProfileManagement,
    permissions: [PERMISSIONS.SUPPLIER_PROFILE_EDIT],
  },
  {
    path: '/user-management/profile-management',
    exact: true,
    name: 'User Management / Profile Management',
    component: ProfileManagement,
    permissions: [
      PERMISSIONS.USER_MAINTENANCE,
      PERMISSIONS.VIEW_USER_DIRECTORY,
    ],
  },
  {
    path: '/search/workspace-search',
    name: 'Search / Workspace',
    component: WorkspaceSearchView,
  },
  {
    path: '/utilities/mapping-control',
    name: 'Utilities / Mapping Control',
    component: MappingControlView,
    permissions: [PERMISSIONS.VIEW_UTILITIES],
  },
  {
    path: '/search/item-search',
    name: 'Search / Item',
    component: ItemSearchView,
    permissions: [PERMISSIONS.ITEM_SEARCH],
  },
];

export default routes;
